import type * as NavigationMenuPrimitive from "@radix-ui/react-navigation-menu";
import type {
  ComponentPropsWithoutRef,
  ElementRef,
  HTMLAttributes,
} from "react";

import type { TIconKey } from "@/ui/components/primitives/Icon";

/*-------------------------------------
|  Constants
|-------------------------------------*/

export const NAVIGATION_MENU_TRIGGER_CLASS = "group inline-flex h-10 w-max items-center justify-center rounded-sys-md bg-surface-default px-4 py-2 font-sys-default text-sys-sm font-medium text-foreground transition-colors hover:bg-surface-elevated focus:bg-surface-elevated focus:outline-none disabled:pointer-events-none disabled:opacity-50 data-[active]:bg-surface-elevated data-[state=open]:bg-surface-elevated";

export const NAVIGATION_MENU_CONTENT_LIST_CLASS = "grid w-[320px]";

/*-------------------------------------
|  Types
|-------------------------------------*/

export type TNavigationMenuElement = ElementRef<
  typeof NavigationMenuPrimitive.Root
>;

export type TNavigationMenuListProps = ComponentPropsWithoutRef<
  typeof NavigationMenuPrimitive.List
>;

export type TNavigationMenuListElement = ElementRef<
  typeof NavigationMenuPrimitive.List
>;

export type TNavigationMenuItemProps = ComponentPropsWithoutRef<
  typeof NavigationMenuPrimitive.Item
>;

export type TNavigationMenuItemElement = ElementRef<
  typeof NavigationMenuPrimitive.Item
>;

export type TNavigationMenuTriggerProps = ComponentPropsWithoutRef<
  typeof NavigationMenuPrimitive.Trigger
>;

export type TNavigationMenuTriggerElement = ElementRef<
  typeof NavigationMenuPrimitive.Trigger
>;

export type TNavigationMenuContentProps = ComponentPropsWithoutRef<
  typeof NavigationMenuPrimitive.Content
>;

export type TNavigationMenuContentElement = ElementRef<
  typeof NavigationMenuPrimitive.Content
>;

export type TNavigationMenuLinkProps = ComponentPropsWithoutRef<
  typeof NavigationMenuPrimitive.Link
> & {
  icon?: TIconKey;
};

export type TNavigationMenuLinkElement = ElementRef<
  typeof NavigationMenuPrimitive.Link
>;

export type TNavigationMenuIndicatorProps = ComponentPropsWithoutRef<
  typeof NavigationMenuPrimitive.Indicator
>;

export type TNavigationMenuIndicatorElement = ElementRef<
  typeof NavigationMenuPrimitive.Indicator
>;

export type TNavigationMenuViewportProps = ComponentPropsWithoutRef<
  typeof NavigationMenuPrimitive.Viewport
>;

export type TNavigationMenuViewportElement = ElementRef<
  typeof NavigationMenuPrimitive.Viewport
>;

export type TNavigationMenuLinkItem = {
  label: string;
  href: string;
  icon?: TIconKey;
  className?: string;
  linkProps?: Omit<
    TNavigationMenuLinkProps,
    "children" | "className" | "href" | "icon"
  >;
};

export type TNavigationMenuDirectLinkItem = TNavigationMenuLinkItem & {
  itemProps?: TNavigationMenuItemProps;
};

export type TNavigationMenuSeparatorProps = HTMLAttributes<HTMLLIElement> & {
  orientation?: "horizontal" | "vertical";
};

export type TNavigationMenuSeparatorItem = {
  type: "separator";
  id: string;
  className?: string;
  orientation?: TNavigationMenuSeparatorProps["orientation"];
  itemProps?: Omit<
    TNavigationMenuSeparatorProps,
    "children" | "className" | "orientation"
  >;
};

export type TNavigationMenuContentItem =
  | TNavigationMenuLinkItem
  | TNavigationMenuSeparatorItem;

export type TNavigationMenuGroupItem = {
  label: string;
  links: readonly TNavigationMenuContentItem[];
  itemProps?: TNavigationMenuItemProps;
  triggerProps?: Omit<TNavigationMenuTriggerProps, "children">;
  contentProps?: TNavigationMenuContentProps;
};

export type TNavigationMenuItem =
  | TNavigationMenuDirectLinkItem
  | TNavigationMenuGroupItem
  | TNavigationMenuSeparatorItem;

export type TNavigationMenuProps = Omit<
  ComponentPropsWithoutRef<typeof NavigationMenuPrimitive.Root>,
  "children"
> & {
  items: readonly TNavigationMenuItem[];
  listProps?: TNavigationMenuListProps;
  viewport?: boolean;
};
